// MARK://dhk8/pos/components/AvatarMenu
// Avatar (iniciales) en el topbar que abre un menú de cuenta. Consolida lo que
// antes ocupaba espacio suelto en el topbar:
//   · el toggle Pruebas/Vivo (EnvSwitcher — solo owner/manager; se auto-oculta
//     para el resto),
//   · Refrescar datos, Tema claro/oscuro, Bloquear terminal, Cerrar sesión.
// El topbar conserva refresh/lock/theme como iconos rápidos; este menú los repite
// (botón "adicional", por pedido de Gustavo) + agrega toggle y logout, en orden
// lógico. Cierra con click-fuera o Escape. Temas vía --pos-* tokens.

import { useEffect, useRef, useState } from 'react'
import { RefreshCw, Lock, LogOut, Sun, Moon } from 'lucide-react'

import { useAuthStore } from '../stores/auth-store.js'
import { useThemeStore } from '../stores/theme.store.js'
import EnvSwitcher from '../features/env/EnvSwitcher.js'

/** "Abraham Vazquez" → "AV"; "Cocina" → "CO"; vacío → "·". */
function initials(name?: string | null): string {
  const parts = (name ?? '').trim().split(/\s+/).filter(Boolean)
  if (parts.length === 0) return '·'
  if (parts.length === 1) return (parts[0]!.slice(0, 2)).toUpperCase()
  return ((parts[0]![0] ?? '') + (parts[parts.length - 1]![0] ?? '')).toUpperCase()
}

interface AvatarMenuProps {
  onRefresh: () => void
  refreshing?: boolean
  onLock: () => void
  onLogout: () => void
}

export default function AvatarMenu({ onRefresh, refreshing, onLock, onLogout }: AvatarMenuProps) {
  const user = useAuthStore((s) => s.user)
  const branches = useAuthStore((s) => s.branches)
  const setActiveBranch = useAuthStore((s) => s.setActiveBranch)
  const theme = useThemeStore((s) => s.theme)
  const toggleTheme = useThemeStore((s) => s.toggleTheme)
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!open) return
    function onDown(e: MouseEvent) { if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false) }
    function onKey(e: KeyboardEvent) { if (e.key === 'Escape') setOpen(false) }
    window.addEventListener('mousedown', onDown)
    window.addEventListener('keydown', onKey)
    return () => { window.removeEventListener('mousedown', onDown); window.removeEventListener('keydown', onKey) }
  }, [open])

  if (!user) return null
  const isDark = theme === 'dark'

  return (
    <div className="pos-avatar" ref={ref}>
      <button
        type="button"
        className="pos-avatar__btn"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={user.fullName}
        title={user.fullName}
        onClick={() => setOpen((v) => !v)}
      >
        <span className="pos-avatar__initials">{initials(user.fullName)}</span>
      </button>

      {open && (
        <div className="pos-avatar__menu" role="menu">
          <div className="pos-avatar__head">
            <span className="pos-avatar__name">{user.fullName}</span>
            {user.role && <span className="pos-avatar__role">{user.role}</span>}
          </div>

          {/* Toggle Pruebas/Vivo — EnvSwitcher se auto-oculta si el rol no puede cambiar entorno. */}
          <div className="pos-avatar__env"><EnvSwitcher /></div>

          {/* Selector de sucursal — sólo si el usuario opera más de una (owner/
              manager de una cadena). El staff de piso ve una sola → no aparece. */}
          {branches.length > 1 && (
            <div className="pos-avatar__branch">
              <label className="pos-avatar__branch-label" htmlFor="pos-branch-select">Sucursal</label>
              <select
                id="pos-branch-select"
                className="pos-avatar__branch-select"
                value={user.branchId}
                onChange={(e) => { const id = e.target.value; if (id !== user.branchId) setActiveBranch(id) }}
              >
                {branches.map((b) => (
                  <option key={b.id} value={b.id}>{b.name}</option>
                ))}
              </select>
            </div>
          )}

          <button
            type="button" className="pos-avatar__item" role="menuitem"
            disabled={refreshing}
            onClick={() => { onRefresh(); setOpen(false) }}
          >
            <RefreshCw size={16} aria-hidden /> Refrescar datos
          </button>
          <button
            type="button" className="pos-avatar__item" role="menuitem"
            onClick={() => toggleTheme()}
          >
            {isDark ? <Sun size={16} aria-hidden /> : <Moon size={16} aria-hidden />}
            {isDark ? 'Tema claro' : 'Tema oscuro'}
          </button>
          <button
            type="button" className="pos-avatar__item" role="menuitem"
            onClick={() => { setOpen(false); onLock() }}
          >
            <Lock size={16} aria-hidden /> Bloquear terminal
          </button>

          <div className="pos-avatar__sep" />
          <button
            type="button" className="pos-avatar__item pos-avatar__item--danger" role="menuitem"
            onClick={() => { setOpen(false); onLogout() }}
          >
            <LogOut size={16} aria-hidden /> Cerrar sesión
          </button>
        </div>
      )}
      <style>{styles}</style>
    </div>
  )
}

const styles = `
  .pos-avatar { position: relative; flex-shrink: 0; }
  .pos-avatar__btn {
    display: inline-flex; align-items: center; justify-content: center;
    width: 36px; height: 36px; padding: 0; cursor: pointer;
    border: 1px solid var(--pos-separator-strong, rgba(255,255,255,.14));
    border-radius: 50%;
    background: var(--pos-tint-fill, color-mix(in oklch, #1F8B3A 16%, transparent));
    color: var(--pos-tint-on, #E8EFE9);
    -webkit-tap-highlight-color: transparent;
    transition: border-color var(--pos-duration-fast) var(--pos-ease-out), transform var(--pos-duration-fast) var(--pos-ease-out);
  }
  .pos-avatar__btn:hover { border-color: var(--pos-tint, #1F8B3A); }
  .pos-avatar__btn:active { transform: scale(.95); }
  .pos-avatar__btn:focus-visible { outline: none; border-color: var(--pos-tint, #1F8B3A); box-shadow: 0 0 0 3px var(--pos-tint-soft); }
  .pos-avatar__initials { font-family: var(--font-display, var(--font-sans)); font-size: 13px; font-weight: var(--pos-weight-semibold, 600); letter-spacing: .02em; }

  .pos-avatar__menu {
    position: absolute; top: calc(100% + 8px); right: 0; z-index: var(--pos-z-toast, 300);
    min-width: 232px; display: flex; flex-direction: column; gap: 2px;
    padding: var(--pos-space-2, 8px);
    background: var(--pos-surface, #161C18);
    border: 1px solid var(--pos-separator-strong, rgba(255,255,255,.14));
    border-radius: var(--pos-radius-lg, 12px);
    box-shadow: var(--pos-shadow-4, 0 24px 60px -16px rgba(0,0,0,.55));
  }
  .pos-avatar__branch { display: flex; flex-direction: column; gap: 4px; padding: var(--pos-space-2, 8px) var(--pos-space-3, 12px); }
  .pos-avatar__branch-label { font-size: var(--pos-text-xxs, 11px); text-transform: uppercase; letter-spacing: .06em; color: var(--pos-fg-tertiary, #7E8C82); font-weight: var(--pos-weight-semibold, 600); }
  .pos-avatar__branch-select {
    appearance: none; width: 100%; cursor: pointer;
    background: var(--pos-elevated, #1E2520); color: var(--pos-fg, #E8EFE9);
    border: 1px solid var(--pos-separator-strong, rgba(255,255,255,.14));
    border-radius: var(--pos-radius-md, 8px);
    padding: 9px 12px; font: inherit; font-size: var(--pos-text-sm, 13px);
    min-height: var(--pos-touch-target, 44px);
  }
  .pos-avatar__branch-select:focus-visible { outline: none; border-color: var(--pos-tint, #1F8B3A); }
  .pos-avatar__head { display: flex; flex-direction: column; gap: 1px; padding: var(--pos-space-2, 8px) var(--pos-space-3, 12px) var(--pos-space-2, 8px); }
  .pos-avatar__name { font-size: var(--pos-text-sm, 13px); font-weight: var(--pos-weight-semibold, 600); color: var(--pos-fg, #E8EFE9); }
  .pos-avatar__role { font-size: var(--pos-text-xxs, 11px); text-transform: capitalize; color: var(--pos-fg-tertiary, #7E8C82); }
  .pos-avatar__env { display: flex; justify-content: center; padding: 2px var(--pos-space-2, 8px) var(--pos-space-2, 8px); }
  .pos-avatar__env:empty { display: none; }
  .pos-avatar__item {
    display: flex; align-items: center; gap: var(--pos-space-3, 12px);
    width: 100%; min-height: 40px; padding: 0 var(--pos-space-3, 12px);
    border: none; border-radius: var(--pos-radius-md, 8px);
    background: transparent; color: var(--pos-fg-secondary, #B6BFB8);
    font: inherit; font-size: var(--pos-text-sm, 13px); text-align: left; cursor: pointer;
    transition: background var(--pos-duration-fast) var(--pos-ease-out), color var(--pos-duration-fast) var(--pos-ease-out);
  }
  .pos-avatar__item:hover { background: var(--pos-elevated, #1E2520); color: var(--pos-fg, #E8EFE9); }
  .pos-avatar__item:disabled { opacity: .5; cursor: default; }
  .pos-avatar__item--danger { color: var(--pos-danger, #E5484D); }
  .pos-avatar__item--danger:hover { background: color-mix(in oklch, var(--pos-danger, #E5484D) 12%, transparent); color: var(--pos-danger, #E5484D); }
  .pos-avatar__sep { height: 1px; margin: var(--pos-space-1, 4px) var(--pos-space-2, 8px); background: var(--pos-separator, rgba(255,255,255,.07)); }
`
